/**
 * PH Restaurant POS - Offline Context
 * Provides offline status, sync state, and offline operations
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { syncService } from '../lib/syncService';
import { 
  menuCache, 
  tablesCache, 
  offlineTickets, 
  syncQueue, 
  getDatabaseStats,
  SyncStatus 
} from '../lib/offlineDb';

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
          setServiceWorkerReady(true);
          
          // Handle updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker?.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('New service worker available');
              }
            });
          });
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SYNC_REQUESTED') {
          syncService.syncAll();
        }
      });
    }
  }, []);

  // Subscribe to sync service events
  useEffect(() => {
    const unsubscribe = syncService.subscribe((event) => {
      switch (event.type) {
        case 'STATUS_CHANGE':
          setIsOnline(event.isOnline);
          break;
        case 'SYNC_START':
          setIsSyncing(true);
          setSyncError(null);
          break;
        case 'SYNC_COMPLETE':
          setIsSyncing(false);
          setLastSyncTime(new Date().toISOString());
          updatePendingCount();
          break;
        case 'SYNC_ERROR':
          setIsSyncing(false);
          setSyncError(event.error);
          break;
        case 'OPERATION_SYNCED':
          updatePendingCount();
          break;
        default:
          break;
      }
    });

    return unsubscribe;
  }, []);

  // Start background sync on mount
  useEffect(() => {
    syncService.startBackgroundSync(30000); // Sync every 30 seconds
    updatePendingCount();
    updateSyncStatus();

    return () => {
      syncService.stopBackgroundSync();
    };
  }, []);

  // Update pending operations count
  const updatePendingCount = useCallback(async () => {
    const stats = await syncQueue.getQueueStats();
    setPendingCount(stats.pending + stats.failed);
  }, []);

  // Update full sync status
  const updateSyncStatus = useCallback(async () => {
    const status = await syncService.getStatus();
    setSyncStatus(status);
    setLastSyncTime(status.lastSyncTime);
  }, []);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOnline) {
      setSyncError('Cannot sync while offline');
      return { success: false, error: 'offline' };
    }
    
    const result = await syncService.syncAll();
    await updateSyncStatus();
    return result;
  }, [isOnline, updateSyncStatus]);

  // Request background sync (via service worker)
  const requestBackgroundSync = useCallback(async (tag = 'sync-all') => {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      const registration = await navigator.serviceWorker.ready;
      await registration.sync.register(tag);
      console.log('Background sync registered:', tag);
    } else {
      // Fallback to immediate sync
      triggerSync();
    }
  }, [triggerSync]);

  // Get cached menu data
  const getCachedMenu = useCallback(async () => {
    const categories = await menuCache.getCategories();
    const items = await menuCache.getMenuItems();
    return { categories, items };
  }, []);

  // Get cached tables
  const getCachedTables = useCallback(async (outletId) => {
    return await tablesCache.getTables(outletId);
  }, []);

  // Create offline ticket
  const createOfflineTicket = useCallback(async (ticketData) => {
    const ticket = await offlineTickets.createTicket(ticketData);
    await updatePendingCount();
    return ticket;
  }, [updatePendingCount]);

  // Add item to offline ticket
  const addItemToTicket = useCallback(async (ticketId, item) => {
    const orderItem = await offlineTickets.addItem(ticketId, item);
    await updatePendingCount();
    return orderItem;
  }, [updatePendingCount]);

  // Process offline payment
  const processOfflinePayment = useCallback(async (ticketId, paymentData) => {
    const payment = await offlineTickets.processPayment(ticketId, paymentData);
    await updatePendingCount();
    return payment;
  }, [updatePendingCount]);

  // Get database stats
  const getDbStats = useCallback(async () => {
    return await getDatabaseStats();
  }, []);

  // Check if should operate in offline mode
  const shouldUseOfflineMode = useCallback(() => {
    return !isOnline;
  }, [isOnline]);

  const value = {
    // Status
    isOnline,
    isSyncing,
    syncStatus,
    pendingCount,
    lastSyncTime,
    syncError,
    serviceWorkerReady,
    
    // Actions
    triggerSync,
    requestBackgroundSync,
    updateSyncStatus,
    
    // Offline data operations
    getCachedMenu,
    getCachedTables,
    createOfflineTicket,
    addItemToTicket,
    processOfflinePayment,
    getDbStats,
    shouldUseOfflineMode,
    
    // Menu cache (for direct access)
    menuCache,
    tablesCache
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
}

export default OfflineContext;
